import React, { useState, useEffect } from 'react';
import {
  Button, Modal, Form, Input, InputNumber, Select,
  DatePicker, Space, Row, Col, message,
  Descriptions, Table, Alert, Progress, Tooltip
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ArrowLeftOutlined,
  FileTextOutlined, DollarOutlined, WarningOutlined, SyncOutlined,
  DownloadOutlined, CreditCardOutlined, ExclamationCircleOutlined,
  LinkOutlined, ShoppingCartOutlined, TruckOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { invoiceService } from '../services/api';
import dayjs from 'dayjs';
import '../styles/Phase5.css';

const { Option } = Select;
const { TextArea } = Input;

const InvoiceDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [matchResult, setMatchResult] = useState(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [matching, setMatching] = useState(false);
  const [paymentForm] = Form.useForm();
  const [rejectForm] = Form.useForm();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoiceData, paymentsData] = await Promise.all([
        invoiceService.getById(id),
        invoiceService.getPayments(id),
      ]);
      setInvoice(invoiceData);
      setPayments(paymentsData);
    } catch (error) {
      message.error('Failed to load invoice');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handlePerformMatch = async () => {
    try {
      setMatching(true);
      const result = await invoiceService.performMatch(id);
      setMatchResult(result);
      fetchData();
      if (result.match_status === 'matched') {
        message.success('Invoice matched successfully');
      } else {
        message.warning(`Match found ${result.discrepancies?.length || 0} discrepancies`);
      }
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to perform match');
    } finally {
      setMatching(false);
    }
  };

  const handleApprove = async () => {
    try {
      await invoiceService.approve(id);
      message.success('Invoice approved');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to approve invoice');
    }
  };

  const handleReject = async (values) => {
    try {
      await invoiceService.reject(id, values.reason);
      message.success('Invoice rejected');
      setRejectModalVisible(false);
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to reject invoice');
    }
  };

  const handleRecordPayment = async (values) => {
    try {
      const paymentData = {
        ...values,
        payment_date: values.payment_date?.toISOString(),
        invoice_id: parseInt(id),
      };
      await invoiceService.recordPayment(id, paymentData);
      message.success('Payment recorded');
      setPaymentModalVisible(false);
      paymentForm.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDownloadFile = async () => {
    try {
      const blob = await invoiceService.downloadFile(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = invoice.file_name || `invoice-${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('Failed to download file');
    }
  };

  const getStatusTagClass = (status) => {
    const classes = {
      received: 'p5-tag--info',
      under_review: 'p5-tag--warning',
      approved: 'p5-tag--success',
      rejected: 'p5-tag--danger',
      paid: 'p5-tag--success',
    };
    return classes[status] || 'p5-tag--neutral';
  };

  const getMatchStatusTagClass = (status) => {
    const classes = {
      pending: 'p5-tag--neutral',
      matched: 'p5-tag--success',
      exception: 'p5-tag--danger',
    };
    return classes[status] || 'p5-tag--neutral';
  };

  const lineItemColumns = [
    {
      title: '#',
      dataIndex: 'line_number',
      key: 'line_number',
      width: 50,
      render: (_, __, idx) => <span className="p5-text-slate">{idx + 1}</span>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => <span className="p5-table-primary">{text}</span>
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
      render: (val) => <span className="p5-font-mono">{val}</span>
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (val) => <span className="p5-font-mono">${val?.toFixed(2)}</span>
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 120,
      align: 'right',
      render: (val) => <span className="p5-font-mono p5-table-primary">${val?.toFixed(2)}</span>
    },
    {
      title: 'Match',
      dataIndex: 'match_status',
      key: 'match_status',
      width: 120,
      render: (status) => {
        const classes = {
          pending: 'p5-tag--neutral',
          matched: 'p5-tag--success',
          price_variance: 'p5-tag--warning',
          quantity_variance: 'p5-tag--danger',
        };
        return status ? (
          <span className={`p5-tag ${classes[status] || 'p5-tag--neutral'}`}>
            {status?.replace('_', ' ').toUpperCase()}
          </span>
        ) : <span className="p5-text-slate">-</span>;
      },
    },
  ];

  const paymentColumns = [
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => <span className="p5-table-secondary">{dayjs(date).format('MMM D, YYYY')}</span>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <span className="p5-font-mono p5-table-primary">${amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    },
    {
      title: 'Method',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method) => method ? <span className="p5-tag p5-tag--neutral">{method.toUpperCase()}</span> : '-'
    },
    {
      title: 'Reference',
      dataIndex: 'reference_number',
      key: 'reference_number',
      render: (ref) => ref ? <span className="p5-font-mono">{ref}</span> : '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span className={`p5-tag ${status === 'completed' ? 'p5-tag--success' : 'p5-tag--neutral'}`}>
          {status?.toUpperCase()}
        </span>
      ),
    },
  ];

  if (loading || !invoice) {
    return (
      <div className="p5-page p5-flex-center" style={{ minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <SyncOutlined spin style={{ fontSize: '2rem', color: 'var(--p5-amber-500)' }} />
          <p className="p5-text-slate p5-mt-md">Loading invoice...</p>
        </div>
      </div>
    );
  }

  const isOverdue = invoice.due_date && dayjs(invoice.due_date).isBefore(dayjs()) && invoice.payment_status !== 'paid';

  return (
    <div className="p5-page">
      {/* Back Button */}
      <Button
        className="p5-btn p5-btn--ghost"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/invoices')}
        style={{ marginBottom: 24 }}
      >
        Back to Invoices
      </Button>

      <Row gutter={32}>
        {/* Main Content */}
        <Col span={16}>
          {/* Invoice Header Card */}
          <div className="p5-card p5-animate-fadeUp">
            <div className="p5-card-body">
              <Row justify="space-between" align="top">
                <Col>
                  <h1 className="p5-font-display" style={{
                    fontSize: '2rem',
                    color: 'var(--p5-navy-900)',
                    margin: '0 0 16px'
                  }}>
                    Invoice #{invoice.invoice_number}
                  </h1>
                  <Space size={8}>
                    <span className={`p5-tag ${getStatusTagClass(invoice.status)}`}>
                      <span className="p5-tag-dot" />
                      {invoice.status?.toUpperCase()}
                    </span>
                    <span className={`p5-tag ${getMatchStatusTagClass(invoice.match_status)}`}>
                      {invoice.match_status?.toUpperCase()}
                    </span>
                    {isOverdue && (
                      <span className="p5-tag p5-tag--danger">
                        <WarningOutlined style={{ marginRight: 4 }} />
                        OVERDUE
                      </span>
                    )}
                  </Space>
                </Col>
                <Col>
                  <Space>
                    {invoice.file_path && (
                      <Button
                        className="p5-btn p5-btn--ghost"
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadFile}
                      >
                        Download
                      </Button>
                    )}
                    {invoice.purchase_order_id && invoice.match_status === 'pending' && (
                      <Button
                        className="p5-btn p5-btn--primary"
                        icon={<SyncOutlined spin={matching} />}
                        onClick={handlePerformMatch}
                        loading={matching}
                      >
                        Run 3-Way Match
                      </Button>
                    )}
                    {invoice.status !== 'approved' && invoice.status !== 'paid' && invoice.status !== 'rejected' && (
                      <>
                        <Button
                          className="p5-btn p5-btn--accent"
                          icon={<CheckCircleOutlined />}
                          onClick={handleApprove}
                        >
                          Approve
                        </Button>
                        <Button
                          className="p5-btn p5-btn--ghost"
                          danger
                          icon={<CloseCircleOutlined />}
                          onClick={() => setRejectModalVisible(true)}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {invoice.status === 'approved' && invoice.balance_due > 0 && (
                      <Button
                        className="p5-btn p5-btn--accent"
                        icon={<CreditCardOutlined />}
                        onClick={() => {
                          paymentForm.setFieldsValue({
                            payment_date: dayjs(),
                            amount: invoice.balance_due,
                            currency: invoice.currency || 'USD',
                          });
                          setPaymentModalVisible(true);
                        }}
                      >
                        Record Payment
                      </Button>
                    )}
                  </Space>
                </Col>
              </Row>
            </div>
          </div>

          {/* Match Discrepancies Alert */}
          {invoice.match_discrepancies && invoice.match_discrepancies.length > 0 && (
            <div className="p5-card p5-animate-fadeUp" style={{
              marginTop: 24,
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(245, 158, 11, 0.02))',
              borderLeft: '4px solid var(--p5-amber-500)'
            }}>
              <div className="p5-card-body">
                <div className="p5-flex p5-gap-sm" style={{ marginBottom: 12 }}>
                  <ExclamationCircleOutlined style={{ color: 'var(--p5-amber-500)', fontSize: '1.25rem' }} />
                  <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)' }}>Match Discrepancies Found</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--p5-navy-700)' }}>
                  {invoice.match_discrepancies.map((d, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {d.description}
                      {d.variance_percentage && (
                        <span className="p5-font-mono" style={{ marginLeft: 8, color: 'var(--p5-amber-500)' }}>
                          ({d.variance_percentage.toFixed(1)}% variance)
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Rejection Reason */}
          {invoice.rejection_reason && (
            <div className="p5-card p5-animate-fadeUp" style={{
              marginTop: 24,
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(239, 68, 68, 0.02))',
              borderLeft: '4px solid var(--p5-rose-500)'
            }}>
              <div className="p5-card-body">
                <div className="p5-flex p5-gap-sm" style={{ marginBottom: 8 }}>
                  <CloseCircleOutlined style={{ color: 'var(--p5-rose-500)', fontSize: '1.25rem' }} />
                  <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)' }}>Rejection Reason</span>
                </div>
                <p style={{ color: 'var(--p5-navy-700)', margin: 0 }}>{invoice.rejection_reason}</p>
              </div>
            </div>
          )}

          {/* Invoice Details */}
          <div className="p5-card p5-animate-fadeUp" style={{ marginTop: 24, animationDelay: '100ms' }}>
            <div className="p5-card-body">
              <h3 className="p5-section-title">
                <FileTextOutlined /> Invoice Details
              </h3>
              <div style={{
                background: 'var(--p5-slate-100)',
                borderRadius: 'var(--p5-radius-md)',
                padding: '20px'
              }}>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="p5-label">Vendor</div>
                    <div style={{ color: 'var(--p5-navy-800)', fontWeight: 500 }}>
                      {invoice.vendor?.name || invoice.client_vendor?.vendor?.name || 'N/A'}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="p5-label">Invoice Date</div>
                    <div style={{ color: 'var(--p5-navy-800)' }}>
                      {dayjs(invoice.invoice_date).format('MMMM D, YYYY')}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="p5-label">Due Date</div>
                    <div style={{ color: isOverdue ? 'var(--p5-rose-500)' : 'var(--p5-navy-800)', fontWeight: isOverdue ? 600 : 400 }}>
                      {invoice.due_date ? dayjs(invoice.due_date).format('MMMM D, YYYY') : 'N/A'}
                      {isOverdue && ' (Overdue)'}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="p5-label">Payment Terms</div>
                    <div style={{ color: 'var(--p5-navy-800)' }}>
                      {invoice.payment_terms || 'N/A'}
                    </div>
                  </Col>
                  {invoice.purchase_order_id && (
                    <Col span={12}>
                      <div className="p5-label">Linked Purchase Order</div>
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--p5-amber-500)',
                          fontWeight: 600,
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onClick={() => navigate(`/purchase-orders`)}
                      >
                        <LinkOutlined />
                        PO #{invoice.purchase_order?.po_number || invoice.purchase_order_id}
                      </button>
                    </Col>
                  )}
                  <Col span={12}>
                    <div className="p5-label">Currency</div>
                    <div style={{ color: 'var(--p5-navy-800)' }}>{invoice.currency}</div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="p5-card p5-animate-fadeUp" style={{ marginTop: 24, animationDelay: '200ms' }}>
            <div className="p5-card-body" style={{ paddingBottom: 0 }}>
              <h3 className="p5-section-title">
                <FileTextOutlined /> Line Items
              </h3>
            </div>
            <div className="p5-table-wrapper" style={{ border: 'none', boxShadow: 'none' }}>
              <Table
                columns={lineItemColumns}
                dataSource={invoice.line_items}
                rowKey="id"
                pagination={false}
                size="small"
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row style={{ background: 'var(--p5-slate-100)' }}>
                      <Table.Summary.Cell index={0} colSpan={4} align="right">
                        <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)' }}>Subtotal</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <span className="p5-font-mono" style={{ fontWeight: 600 }}>${invoice.subtotal?.toFixed(2)}</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} />
                    </Table.Summary.Row>
                    {invoice.tax_amount > 0 && (
                      <Table.Summary.Row style={{ background: 'var(--p5-slate-100)' }}>
                        <Table.Summary.Cell index={0} colSpan={4} align="right">Tax</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right" className="p5-font-mono">${invoice.tax_amount?.toFixed(2)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2} />
                      </Table.Summary.Row>
                    )}
                    {invoice.shipping_amount > 0 && (
                      <Table.Summary.Row style={{ background: 'var(--p5-slate-100)' }}>
                        <Table.Summary.Cell index={0} colSpan={4} align="right">Shipping</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right" className="p5-font-mono">${invoice.shipping_amount?.toFixed(2)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2} />
                      </Table.Summary.Row>
                    )}
                    {invoice.discount_amount > 0 && (
                      <Table.Summary.Row style={{ background: 'var(--p5-slate-100)' }}>
                        <Table.Summary.Cell index={0} colSpan={4} align="right">Discount</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right" className="p5-font-mono" style={{ color: 'var(--p5-emerald-500)' }}>-${invoice.discount_amount?.toFixed(2)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2} />
                      </Table.Summary.Row>
                    )}
                    <Table.Summary.Row style={{ background: 'var(--p5-navy-900)' }}>
                      <Table.Summary.Cell index={0} colSpan={4} align="right">
                        <span style={{ fontWeight: 600, color: 'var(--p5-slate-200)' }}>Total</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <span className="p5-font-display" style={{ fontSize: '1.25rem', color: 'var(--p5-amber-400)' }}>${invoice.total_amount?.toFixed(2)}</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} />
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </div>
          </div>

          {/* Payments */}
          {payments.length > 0 && (
            <div className="p5-card p5-animate-fadeUp" style={{ marginTop: 24, animationDelay: '300ms' }}>
              <div className="p5-card-body" style={{ paddingBottom: 0 }}>
                <h3 className="p5-section-title">
                  <CreditCardOutlined /> Payment History
                </h3>
              </div>
              <div className="p5-table-wrapper" style={{ border: 'none', boxShadow: 'none' }}>
                <Table
                  columns={paymentColumns}
                  dataSource={payments}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </div>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div className="p5-card p5-animate-fadeUp" style={{ marginTop: 24, animationDelay: '400ms' }}>
              <div className="p5-card-body">
                <h3 className="p5-section-title">Notes</h3>
                <p style={{ color: 'var(--p5-navy-700)', margin: 0, lineHeight: 1.6 }}>{invoice.notes}</p>
              </div>
            </div>
          )}
        </Col>

        {/* Sidebar */}
        <Col span={8}>
          {/* Payment Summary Card */}
          <div className="p5-card p5-card--featured p5-animate-fadeUp" style={{ animationDelay: '100ms' }}>
            <div className="p5-card-body">
              <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '8px' }}>Total Amount</div>
              <div className="p5-font-display" style={{ fontSize: '2.5rem', color: 'var(--p5-amber-400)', marginBottom: '24px' }}>
                ${invoice.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>

              <Row gutter={16}>
                <Col span={12}>
                  <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '4px' }}>Paid</div>
                  <div className="p5-font-display" style={{ fontSize: '1.5rem', color: 'var(--p5-teal-400)' }}>
                    ${invoice.amount_paid?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '4px' }}>Balance Due</div>
                  <div className="p5-font-display" style={{
                    fontSize: '1.5rem',
                    color: invoice.balance_due > 0 ? 'var(--p5-rose-400)' : 'var(--p5-teal-400)'
                  }}>
                    ${invoice.balance_due?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </Col>
              </Row>

              {invoice.total_amount > 0 && (
                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '8px' }}>Payment Progress</div>
                  <Progress
                    percent={Math.round((invoice.amount_paid / invoice.total_amount) * 100)}
                    status={invoice.payment_status === 'paid' ? 'success' : 'active'}
                    strokeColor={{
                      '0%': 'var(--p5-amber-500)',
                      '100%': 'var(--p5-teal-500)',
                    }}
                    trailColor="rgba(255,255,255,0.1)"
                  />
                </div>
              )}
            </div>
          </div>

          {/* 3-Way Match Status */}
          {invoice.purchase_order_id && (
            <div className="p5-card p5-animate-fadeUp" style={{ marginTop: 24, animationDelay: '200ms' }}>
              <div className="p5-card-body">
                <h3 className="p5-section-title" style={{ fontSize: '1.125rem' }}>
                  <SyncOutlined /> 3-Way Match Status
                </h3>

                {/* Match Visualization */}
                <div className="p5-match-indicator">
                  <div className={`p5-match-node ${invoice.purchase_order_id ? 'p5-match-node--matched' : ''}`}>
                    <ShoppingCartOutlined />
                  </div>
                  <div className={`p5-match-connector ${invoice.purchase_order_id ? 'p5-match-connector--matched' : ''}`} />
                  <div className={`p5-match-node ${invoice.goods_receipt_id ? 'p5-match-node--matched' : 'p5-match-node--exception'}`}>
                    <TruckOutlined />
                  </div>
                  <div className={`p5-match-connector ${invoice.match_status === 'matched' ? 'p5-match-connector--matched' : ''}`} />
                  <div className={`p5-match-node ${invoice.match_status === 'matched' ? 'p5-match-node--matched' : invoice.match_status === 'exception' ? 'p5-match-node--exception' : ''}`}>
                    <FileTextOutlined />
                  </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <Row justify="space-between" style={{ marginBottom: '12px' }}>
                    <Col><span style={{ color: 'var(--p5-navy-700)' }}>Purchase Order</span></Col>
                    <Col>
                      <span className="p5-tag p5-tag--success">
                        <CheckCircleOutlined /> Linked
                      </span>
                    </Col>
                  </Row>
                  <Row justify="space-between" style={{ marginBottom: '12px' }}>
                    <Col><span style={{ color: 'var(--p5-navy-700)' }}>Goods Receipt</span></Col>
                    <Col>
                      {invoice.goods_receipt_id ? (
                        <span className="p5-tag p5-tag--success">
                          <CheckCircleOutlined /> Linked
                        </span>
                      ) : (
                        <span className="p5-tag p5-tag--warning">
                          <WarningOutlined /> Not Linked
                        </span>
                      )}
                    </Col>
                  </Row>
                  <Row justify="space-between">
                    <Col><span style={{ color: 'var(--p5-navy-700)' }}>Match Status</span></Col>
                    <Col>
                      <span className={`p5-tag ${getMatchStatusTagClass(invoice.match_status)}`}>
                        {invoice.match_status?.toUpperCase()}
                      </span>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          )}

          {/* Activity Timeline */}
          <div className="p5-card p5-animate-fadeUp" style={{ marginTop: 24, animationDelay: '300ms' }}>
            <div className="p5-card-body">
              <h3 className="p5-section-title" style={{ fontSize: '1.125rem' }}>Activity</h3>
              <div className="p5-timeline">
                <div className="p5-timeline-item">
                  <div className="p5-timeline-dot p5-timeline-dot--completed" />
                  <div className="p5-timeline-content">
                    <div className="p5-timeline-title">Invoice Received</div>
                    <div className="p5-timeline-date">
                      {dayjs(invoice.created_at).format('MMM D, YYYY h:mm A')}
                    </div>
                  </div>
                </div>
                {invoice.approved_at && (
                  <div className="p5-timeline-item">
                    <div className="p5-timeline-dot p5-timeline-dot--completed" />
                    <div className="p5-timeline-content">
                      <div className="p5-timeline-title">Approved</div>
                      <div className="p5-timeline-date">
                        {dayjs(invoice.approved_at).format('MMM D, YYYY h:mm A')}
                      </div>
                    </div>
                  </div>
                )}
                {payments.map((p, idx) => (
                  <div className="p5-timeline-item" key={idx}>
                    <div className="p5-timeline-dot" />
                    <div className="p5-timeline-content">
                      <div className="p5-timeline-title">Payment Recorded</div>
                      <div style={{ color: 'var(--p5-emerald-500)', fontFamily: 'var(--p5-font-mono)', fontWeight: 600 }}>
                        ${p.amount?.toLocaleString()}
                      </div>
                      <div className="p5-timeline-date">
                        {dayjs(p.payment_date).format('MMM D, YYYY')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Payment Modal */}
      <Modal
        title={null}
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        className="p5-modal"
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            Record Payment
          </h2>
        </div>
        <Form form={paymentForm} layout="vertical" onFinish={handleRecordPayment}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="payment_date" label={<span className="p5-label">Payment Date</span>} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amount" label={<span className="p5-label">Amount</span>} rules={[{ required: true }]}>
                <InputNumber min={0.01} max={invoice.balance_due} precision={2} prefix="$" style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="payment_method" label={<span className="p5-label">Payment Method</span>}>
                <Select placeholder="Select method" size="large">
                  <Option value="ach">ACH</Option>
                  <Option value="wire">Wire Transfer</Option>
                  <Option value="check">Check</Option>
                  <Option value="credit_card">Credit Card</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reference_number" label={<span className="p5-label">Reference #</span>}>
                <Input placeholder="Check number, transaction ID, etc." size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label={<span className="p5-label">Notes</span>}>
            <TextArea rows={2} size="large" />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button className="p5-btn p5-btn--ghost" onClick={() => setPaymentModalVisible(false)}>Cancel</Button>
            <Button className="p5-btn p5-btn--accent" htmlType="submit">Record Payment</Button>
          </Space>
        </Form>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title={null}
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        footer={null}
        className="p5-modal"
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            Reject Invoice
          </h2>
        </div>
        <Form form={rejectForm} layout="vertical" onFinish={handleReject}>
          <Form.Item
            name="reason"
            label={<span className="p5-label">Rejection Reason</span>}
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea rows={4} placeholder="Explain why this invoice is being rejected..." size="large" />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button className="p5-btn p5-btn--ghost" onClick={() => setRejectModalVisible(false)}>Cancel</Button>
            <Button className="p5-btn p5-btn--ghost" danger htmlType="submit">Reject Invoice</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default InvoiceDetail;
